import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AppState} from "../../../../../shared/store/store";
import {Store} from "@ngrx/store";
import {Observable, withLatestFrom} from 'rxjs';
import {Serializer, Serializers} from "../../../../../shared/interfaces/serializer";
import {DataSetMetric, DataSetMetricsGroup} from '../../../../../shared/interfaces/metrics';
import {
  AgGridComponentFrameworkComponent
} from "../../../../../shared/components/ag-grid/ag-grid-component-framework/ag-grid-component-framework.component";
import {CellSourceComponent} from "../../../../../shared/components/cell-source/cell-source.component";
import {CellSelectComponent} from "../../../../../shared/components/cell-select/cell-select.component";
import {
  AgGridTemplateFrameworkComponent
} from "../../../../../shared/components/ag-grid/ag-grid-template-framework/ag-grid-template-framework.component";
import {TranslateService} from "@ngx-translate/core";
import {AppService} from "../../../../../shared/services/app.service";
import {SelectOption} from "../../../../../shared/interfaces/form";
import {Visibility} from "../../../../../shared/interfaces/settings";
import {SettingsService} from "../../../../../shared/services/settings.service";
import {filter, map} from 'rxjs/operators';
import {dialogOpen} from "../../../../../shared/store/dialog/dialog.actions";
import {DialogConfirmComponent} from "../../../../../shared/components/dialog/dialog-confirm/dialog-confirm.component";
import {
  CreateCalculatedMetricDialogComponent
} from "../create-calculated-metric-dialog/create-calculated-metric-dialog.component";
import {
  selectDataSetMetricCalculatedComponentObjects,
  selectDataSetMetricsCalculated,
  selectDataSetMetricsLoading
} from '../../../../../shared/store/dataSetMetrics/dataSetMetrics.selectors';
import {
  selectDataSetMetricsGroupComponentObjects,
  selectDataSetMetricsGroups,
  selectDataSetMetricsGroupsLoaded
} from '../../../../../shared/store/dataSetMetricsGroups/dataSetMetricsGroups.selectors';
import {ColDef} from '@ag-grid-community/core';
import {
  deleteDataSetMetric,
  deleteDataSetMetricSuccess,
  updateDataSetMetric
} from '../../../../../shared/store/dataSetMetrics/dataSetMetrics.actions';
import {
  DatasetMetricsGroupComponentObject,
  DatasetMetricsGroupComponentObjects
} from '../../../../../shared/classes/dataset-metrics-group-component-object';
import {MetricComponentObject, MetricComponentObjects} from '../../../../../shared/classes/metric-component-object';

@Component({
  selector: 'app-metrics-calculated-table',
  templateUrl: './metrics-calculated-table.component.html',
  styleUrls: ['./metrics-calculated-table.component.scss']
})
export class MetricsCalculatedTableComponent implements OnInit {
  @ViewChild('calculatedActionsColumn') private readonly calculatedActionsT:  TemplateRef<any>;

  public readonly defaultColDef: any = {
    resizable: true,
    suppressNavigable: true,
    suppressCellSelection: true,
    menuTabs: ['filterMenuTab'],
    filterParams: {
      buttons: ['reset']
    }
  };

  public columnDefs$: Observable<Array<ColDef>> = this._store.select(selectDataSetMetricsGroupsLoaded).pipe(
    filter((loaded: boolean): boolean => loaded),
    withLatestFrom(this._store.select(selectDataSetMetricsGroupComponentObjects)),
    map(([_, groups]: [boolean, DatasetMetricsGroupComponentObjects]): Array<ColDef> => {
      return [
        {
          minWidth: 200,
          sortable: true,
          filter: 'agTextColumnFilter',
          lockPosition: true,
          headerValueGetter: _ => this._translateS.instant('settings.metric'),
          valueGetter: params => params.data.payload.attributes.name,
          filterValueGetter: params => params.data.payload.attributes.name,
          cellRendererFramework: AgGridComponentFrameworkComponent,
          cellRendererParams: _ => {
            return {
              component:  CellSourceComponent,
              componentParams: {
                sourceGetter: (row: Serializer<DataSetMetric>) => row
              }
            };
          }
        },
        {
          minWidth: 200,
          sortable: true,
          filter: 'agTextColumnFilter',
          lockPosition: true,
          headerValueGetter: _ => this._translateS.instant('settings.operation'),
          valueGetter: params => params.data.payload.attributes.operation,
          filterValueGetter: params => params.data.payload.attributes.operation
        },
        {
          minWidth: 200,
          sortable: true,
          filter: true,
          lockPosition: true,
          tooltipComponent: 'customTooltip',
          headerTooltip: this._translateS.instant('settings.visibility_tooltip'),
          headerValueGetter: _ => this._translateS.instant('settings.visibility'),
          valueGetter: params => params.data.payload.attributes.visibility,
          cellRendererFramework: AgGridComponentFrameworkComponent,
          cellRendererParams: _ => {
            return {
              component:  CellSelectComponent,
              componentParams: this._getVisibilityParams()
            };
          },
          filterValueGetter: params => this._translateS.instant(`settings.${params.data.payload.attributes.visibility}`)
        },
        {
          minWidth: 200,
          sortable: true,
          filter: 'agTextColumnFilter',
          lockPosition: true,
          headerValueGetter: _ => this._translateS.instant('settings.category'),
          valueGetter: params => params.data.payload.relationships.data_set_metrics_group.data?.attributes.name,
          filterValueGetter: params => params.data.payload.relationships.data_set_metrics_group.data?.attributes.name || this._translateS.instant('settings.source_menu_only'),
          cellRendererFramework: AgGridComponentFrameworkComponent,
          cellRendererParams: _ => {
            return {
              component:  CellSelectComponent,
              componentParams: this._getCategoryParams(groups)
            };
          }
        },
        {
          minWidth: 200,
          sortable: false,
          filter: false,
          lockPosition: true,
          suppressMenu: true,
          cellRendererFramework: AgGridTemplateFrameworkComponent,
          cellRendererParams: _ => {
            return {
              ngTemplate: this.calculatedActionsT
            };
          }
        }
      ];
    })
  );

  public metrics$: Observable<MetricComponentObjects> = this._store.select(selectDataSetMetricCalculatedComponentObjects);
  public loading$: Observable<boolean> = this._store.select(selectDataSetMetricsLoading);

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _translateS: TranslateService,
    private readonly _settingsS: SettingsService,
    public readonly appS: AppService
  ) { }

  ngOnInit(): void {}

  private _getVisibilityParams(): any {
    return {
      options:            this._settingsS.visibilities,
      disabled:           this.appS.isNotDatasetAdmin,
      defaultValueGetter: (metric: MetricComponentObject) => (metric.payload.attributes as DataSetMetric).visibility,
      textGetter:         (visibility: SelectOption<Visibility, string>) => visibility.text,
      valueGetter:        (visibility: SelectOption<Visibility, string>) => visibility.key,
      onValueChanges:     (row: any, value: any) => this._updateMetric(row, { visibility: value })
    };
  }

  private _getCategoryParams(metricGroups: DatasetMetricsGroupComponentObjects): any {
    return {
      options:            metricGroups,
      disabled:           this.appS.isNotDatasetAdmin,
      defaultValueGetter: (category: MetricComponentObject) => {
        return category.payload.relationships.data_set_metrics_group.data?.id || '0';
      },
      textGetter:         (category: DatasetMetricsGroupComponentObject) => category.payload.attributes.name,
      valueGetter:        (category: DatasetMetricsGroupComponentObject) => category.payload.id,
      onValueChanges:     (row: any, value: any) => this._updateMetric(row, { data_set_metrics_group_id: value })
    };
  }

  private _updateMetric(metric: MetricComponentObject, option: Partial<DataSetMetric>): void {
    this._store.dispatch(updateDataSetMetric({
      metric: metric.payload as Serializer<DataSetMetric>,
      update: option
    }));
  }

  public onUpdate(metric: MetricComponentObject): void {
    this._store.dispatch(dialogOpen({
      component: CreateCalculatedMetricDialogComponent,
      config: {
        width: '750px',
        height: 'auto',
        disableClose: true
      },
      data: {
        metric: metric.payload
      }
    }))
  }

  public onDelete(metric: MetricComponentObject): void {
    this._store.dispatch(dialogOpen({
      component: DialogConfirmComponent,
      config: {
        width: '500px',
        height: 'auto',
        disableClose: true
      },
      data: {
        title: 'metrics.delete_calculated_metric_title',
        message: 'metrics.delete_calculated_metric_message',
        type: 'delete',
        item: metric,
        startActions: [deleteDataSetMetric],
        successActions: [deleteDataSetMetricSuccess],
        onSubmit: () => {
          this._store.dispatch(deleteDataSetMetric({
            metric: metric.payload as Serializer<DataSetMetric>
          }));
        }
      }
    }))
  }
}
